import React, { useMemo, useState, useContext } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { routeDurationSec, routeProgressLabel } from "../utils";
import { PieChart } from "react-minimal-pie-chart";
import StarekContext from "../context/StarekContext";

const INACTIVITY_SECS   = 5  * 60;
const UNRESPONSIVE_SECS = 60 * 60;
const STALE_FACTOR      = 10;
const MIN_BATTERY       = 95;

// ── helpers ──────────────────────────────────────────────────────────

function productionStatus(device) {
    const now     = moment();
    const secsAgo = now.diff(moment(device?.device?.lastSeen?.master || 0), "seconds");
    if (secsAgo > INACTIVITY_SECS) return "offline";
    const activeJob = (device.jobSchedules || []).find((js) =>
        now.isSameOrAfter(moment(js.start_time)) && now.isSameOrBefore(moment(js.end_time)),
    );
    if (!activeJob) return "idle";
    const cycSec = routeDurationSec(activeJob);
    if (cycSec && secsAgo > cycSec * STALE_FACTOR) return "stopped";
    return "producing";
}

function lastSeenLabel(ms) {
    if (!ms) return "never";
    const d = moment().diff(moment(ms), "seconds");
    if (d < 60)   return `${d}s ago`;
    if (d < 3600) return `${Math.floor(d / 60)}m ago`;
    return `${Math.floor(d / 3600)}h ${Math.floor((d % 3600) / 60)}m ago`;
}

function downtimeDuration(fromIso) {
    if (!fromIso) return null;
    const mins = moment().diff(moment(fromIso), "minutes");
    if (mins < 60)  return `${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function parseAttrs(raw) {
    if (!raw) return {};
    if (typeof raw === "object") return raw;
    try { return JSON.parse(raw); } catch { return {}; }
}

// ── status config ─────────────────────────────────────────────────────

const STATUS = {
    maintenance: { label: "In Maintenance", borderColor: "#6f42c1", headerBg: "#ede5ff", headerText: "#3d1f8f", icon: "fa-wrench"       },
    stopped:     { label: "Stopped",        borderColor: "#dc3545", headerBg: "#f8d7da", headerText: "#721c24", icon: "fa-stop-circle"  },
    idle:        { label: "Idle",           borderColor: "#e67e22", headerBg: "#fdf3e3", headerText: "#7d4500", icon: "fa-pause-circle" },
    producing:   { label: "Active",          borderColor: "#28a745", headerBg: "#d4edda", headerText: "#155724", icon: "fa-play-circle"  },
    offline:     { label: "Offline",        borderColor: "#6c757d", headerBg: "#e2e3e5", headerText: "#383d41", icon: "fa-times-circle" },
};

// maintenance first — it's an acknowledged known state; stopped next as unknown problem
const STATUS_ORDER = ["maintenance", "stopped", "idle", "producing", "offline"];

// ── MachineCard ───────────────────────────────────────────────────────

function MachineCard({ dev }) {
    const s = STATUS[dev.status];
    const isMaintenance = dev.status === "maintenance";
    const isStopped     = dev.status === "stopped";

    return (
        <div style={{
            border: `2px solid ${s.borderColor}`,
            borderRadius: 8,
            overflow: "hidden",
            width: 190,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            background: "#fff",
            boxShadow: (isMaintenance || isStopped)
                ? `0 0 0 3px ${s.borderColor}30`
                : "0 1px 4px rgba(0,0,0,0.07)",
        }}>
            {/* status strip */}
            <div style={{ background: s.headerBg, padding: "5px 10px", display: "flex", alignItems: "center", gap: 5 }}>
                <i className={`fa ${s.icon}`} style={{ color: s.borderColor, fontSize: "0.78rem" }} />
                <span style={{ fontWeight: 700, fontSize: "0.68rem", letterSpacing: "0.06em", textTransform: "uppercase", color: s.headerText }}>
                    {s.label}
                </span>
                {isStopped && dev.silentMins > 0 && (
                    <span style={{ marginLeft: "auto", fontSize: "0.63rem", fontWeight: 700, color: "#721c24", background: "rgba(220,53,69,0.15)", padding: "1px 5px", borderRadius: 3 }}>
                        {dev.silentMins}m silent
                    </span>
                )}
                {isMaintenance && dev.downtimeDuration && (
                    <span style={{ marginLeft: "auto", fontSize: "0.63rem", fontWeight: 700, color: "#3d1f8f", background: "rgba(111,66,193,0.12)", padding: "1px 5px", borderRadius: 3 }}>
                        {dev.downtimeDuration}
                    </span>
                )}
            </div>

            {/* body */}
            <div style={{ padding: "10px 12px 8px", flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ fontWeight: 700, fontSize: "0.92rem", color: "#212529" }}>
                    {dev.name || dev.id}
                </div>

                {/* Maintenance: show reason prominently */}
                {isMaintenance && dev.maintenanceReason && (
                    <div style={{ fontSize: "0.75rem", color: "#3d1f8f", background: "rgba(111,66,193,0.07)", borderRadius: 4, padding: "3px 6px", marginTop: 2 }}>
                        <i className="fa fa-comment mr-1" style={{ fontSize: "0.65rem" }} />
                        {dev.maintenanceReason}
                    </div>
                )}

                {/* Production: show part + route progress */}
                {!isMaintenance && (
                    <div style={{ fontSize: "0.76rem", color: "#343a40", minHeight: 30 }}>
                        {dev.partName ? (
                            <>
                                <div>
                                    <i className="fa fa-cube mr-1" style={{ color: "#495057", fontSize: "0.68rem" }} />
                                    <strong>{dev.partName}</strong>
                                </div>
                                {dev.progressLabel && (
                                    <div style={{ color: "#495057", marginTop: 1 }}>
                                        <i className="fa fa-tasks mr-1" style={{ fontSize: "0.68rem" }} />
                                        {dev.progressLabel}
                                    </div>
                                )}
                            </>
                        ) : (
                            <span style={{ color: "#868e96", fontStyle: "italic" }}>
                                {dev.status === "idle"    && "No job scheduled"}
                                {dev.status === "offline" && "Unreachable"}
                            </span>
                        )}
                    </div>
                )}

                {/* Last seen */}
                <div style={{
                    fontSize: "0.68rem",
                    color: dev.status === "offline" ? "#721c24" : "#495057",
                    fontWeight: dev.status === "offline" ? 600 : 400,
                    marginTop: "auto",
                    paddingTop: 4,
                    borderTop: "1px solid #f0f0f0",
                }}>
                    <i className="fa fa-clock-o mr-1" style={{ fontSize: "0.62rem" }} />
                    {isMaintenance && dev.maintenanceFrom
                        ? `Down since ${moment(dev.maintenanceFrom).format("h:mma")}`
                        : lastSeenLabel(dev.lastSeenMs)
                    }
                </div>
            </div>
        </div>
    );
}

// ── main component ────────────────────────────────────────────────────

function CardSpinner() {
    return (
        <div style={{
            position: "absolute", inset: 0, background: "rgba(255,255,255,0.8)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 10, borderRadius: "inherit",
        }}>
            <i className="fa fa-spinner fa-spin" style={{ fontSize: "2rem", color: "#6c757d" }} />
        </div>
    );
}

function MachineStatusCard({ devices, maintenanceSchedules, loading }) {
    const [collapsed, setCollapsed] = useState(false);
    const { types } = useContext(StarekContext);
    const TYPE_ADHOC = types?.maintenance_schedules?.schedule_types?.ADHOC ?? 1;

    // Build map of deviceId → active ADHOC maintenance record
    const activeAdhocByDevice = useMemo(() => {
        const map = {};
        (maintenanceSchedules || []).forEach((ms) => {
            if (ms.schedule_type !== TYPE_ADHOC) return;
            const attrs = parseAttrs(ms.schedule_attributes);
            // Active = has a start time but no end time
            if (!attrs.from || attrs.to) return;
            const deviceId = ms.device_id;
            if (!deviceId) return;
            // If multiple open records for same device, keep the most recent
            if (!map[deviceId] || moment(attrs.from) > moment(parseAttrs(map[deviceId].schedule_attributes).from)) {
                map[deviceId] = ms;
            }
        });
        return map;
    }, [maintenanceSchedules, TYPE_ADHOC]);

    const enriched = useMemo(() => {
        return (devices || []).map((device) => {
            const lastSeenMs  = device?.device?.lastSeen?.master || 0;
            const secsAgo     = moment().diff(moment(lastSeenMs), "seconds");
            const now         = moment();
            const activeJob   = (device.jobSchedules || []).find((js) =>
                now.isSameOrAfter(moment(js.start_time)) && now.isSameOrBefore(moment(js.end_time)),
            );

            // Check active ADHOC maintenance first — overrides production status
            const adhoc = activeAdhocByDevice[device.id];
            if (adhoc) {
                const attrs = parseAttrs(adhoc.schedule_attributes);
                return {
                    id:                 device.id,
                    name:               device.name,
                    status:             "maintenance",
                    lastSeenMs,
                    secsAgo,
                    partName:           null,
                    progressLabel:      null,
                    silentMins:         0,
                    // The typed reason, not the generated record name. Records made
                    // before the reason was required simply show none.
                    maintenanceReason:  adhoc.description || attrs.reason || null,
                    maintenanceFrom:    attrs.from  || null,
                    downtimeDuration:   downtimeDuration(attrs.from),
                };
            }

            const status = productionStatus(device);
            return {
                id:                 device.id,
                name:               device.name,
                status,
                lastSeenMs,
                secsAgo,
                partName:           activeJob?.part?.name  || null,
                progressLabel:      routeProgressLabel(activeJob),
                silentMins:         status === "stopped" ? Math.round(secsAgo / 60) : 0,
                maintenanceReason:  null,
                maintenanceFrom:    null,
                downtimeDuration:   null,
            };
        });
    }, [devices, activeAdhocByDevice]);

    const sorted = useMemo(() => {
        const order = { maintenance: 0, stopped: 1, idle: 2, producing: 3, offline: 4 };
        return [...enriched].sort(
            (a, b) => order[a.status] - order[b.status] || (a.name || "").localeCompare(b.name || ""),
        );
    }, [enriched]);

    const counts = useMemo(() => {
        const c = { maintenance: 0, stopped: 0, idle: 0, producing: 0, offline: 0 };
        enriched.forEach((d) => c[d.status]++);
        return c;
    }, [enriched]);

    // Health pie — connectivity perspective (separate from production status)
    const healthData = useMemo(() => {
        let ok = 0, inactive = 0, unresponsive = 0, lowBattery = 0;
        (devices || []).forEach((d) => {
            const secsAgo   = moment().diff(moment(d?.device?.lastSeen?.master || 0), "seconds");
            const battLevel = d?.device?.runtime?.attributes?.battery_level;
            if      (secsAgo >= UNRESPONSIVE_SECS) unresponsive++;
            else if (secsAgo >= INACTIVITY_SECS)   inactive++;
            else                                   ok++;
            if (battLevel != null && battLevel < MIN_BATTERY) lowBattery++;
        });
        return { ok, inactive, unresponsive, lowBattery };
    }, [devices]);

    const pieData = useMemo(() => [
        { title: "Reachable",    value: healthData.ok,           color: "#28a745" },
        { title: "Inactive",     value: healthData.inactive,     color: "#fd7e14" },
        { title: "Unresponsive", value: healthData.unresponsive, color: "#dc3545" },
    ].filter((d) => d.value > 0), [healthData]);

    return (
        <div className={`card card-outline card-info ${collapsed ? "collapsed-card" : ""}`} style={{ position: "relative" }}>
            {loading && <CardSpinner />}
            <div className="card-header py-2">
                <h3 className="card-title">
                    <i className="fa fa-tachometer mr-2" />
                    Fleet Overview
                </h3>
                <div className="card-tools d-flex align-items-center" style={{ gap: 5 }}>
                    {counts.maintenance > 0 && (
                        <span className="badge" style={{ background: "#6f42c1", color: "#fff", fontSize: "0.7rem" }}>
                            {counts.maintenance} in maintenance
                        </span>
                    )}
                    {counts.stopped   > 0 && <span className="badge badge-danger"    style={{ fontSize: "0.7rem" }}>{counts.stopped} stopped</span>}
                    {counts.idle      > 0 && <span className="badge badge-warning"   style={{ fontSize: "0.7rem", background: "#e67e22" }}>{counts.idle} idle</span>}
                    {counts.producing > 0 && <span className="badge badge-success"   style={{ fontSize: "0.7rem" }}>{counts.producing} active</span>}
                    {counts.offline   > 0 && <span className="badge badge-secondary" style={{ fontSize: "0.7rem" }}>{counts.offline} offline</span>}
                    <button type="button" className="btn btn-tool ml-1" onClick={() => setCollapsed(!collapsed)}>
                        <i className={`fas ${collapsed ? "fa-plus" : "fa-minus"}`} />
                    </button>
                </div>
            </div>

            <div className={`card-body p-0 ${collapsed ? "d-none" : ""}`}>
                <div style={{ display: "flex", minHeight: 0 }}>

                    {/* ── Left: machine cards ─────────────────────────── */}
                    <div style={{ flex: 1, minWidth: 0, overflowX: "auto", padding: "12px 16px" }}>
                        {sorted.length === 0 ? (
                            <p className="text-center text-muted small mb-0 py-4">No devices found.</p>
                        ) : (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                                {sorted.map((dev) => <MachineCard key={dev.id} dev={dev} />)}
                            </div>
                        )}
                    </div>

                    {/* ── Divider ─────────────────────────────────────── */}
                    <div style={{ width: 1, background: "#dee2e6", flexShrink: 0 }} />

                    {/* ── Right: connectivity health pie ───────────────── */}
                    <div style={{ width: 220, flexShrink: 0, padding: "16px 14px", display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <div style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#495057", marginBottom: 10 }}>
                            Connectivity Health
                        </div>

                        {pieData.length > 0 ? (
                            <PieChart data={pieData} style={{ height: 130 }} />
                        ) : (
                            <div style={{ height: 130, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <span className="text-muted small">No data</span>
                            </div>
                        )}

                        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 5, width: "100%" }}>
                            {[
                                { label: "Reachable",    value: healthData.ok,           color: "#28a745" },
                                { label: "Inactive",     value: healthData.inactive,     color: "#fd7e14" },
                                { label: "Unresponsive", value: healthData.unresponsive, color: "#dc3545" },
                            ].map(({ label, value, color }) => value > 0 && (
                                <div key={label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
                                        <span style={{ fontSize: "0.73rem", color: "#495057" }}>{label}</span>
                                    </div>
                                    <span style={{ fontSize: "0.73rem", fontWeight: 700, color: "#212529" }}>{value}</span>
                                </div>
                            ))}
                            {healthData.lowBattery > 0 && (
                                <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 4, paddingTop: 4, borderTop: "1px solid #dee2e6" }}>
                                    <i className="fa fa-battery-quarter" style={{ color: "#fd7e14", fontSize: "0.7rem" }} />
                                    <span style={{ fontSize: "0.73rem", color: "#495057" }}>Low battery</span>
                                    <span style={{ fontSize: "0.73rem", fontWeight: 700, color: "#212529", marginLeft: "auto" }}>{healthData.lowBattery}</span>
                                </div>
                            )}
                        </div>

                        {counts.maintenance > 0 && (
                            <div style={{ marginTop: 14, paddingTop: 10, borderTop: "1px solid #dee2e6", width: "100%" }}>
                                <div style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#3d1f8f", marginBottom: 6 }}>
                                    <i className="fa fa-wrench mr-1" />
                                    Active Downtime
                                </div>
                                {sorted.filter((d) => d.status === "maintenance").map((d) => (
                                    <div key={d.id} style={{ fontSize: "0.72rem", color: "#495057", marginBottom: 4 }}>
                                        <span style={{ fontWeight: 600, color: "#212529" }}>{d.name || d.id}</span>
                                        {d.downtimeDuration && (
                                            <span style={{ marginLeft: 5, color: "#6f42c1", fontWeight: 600 }}>{d.downtimeDuration}</span>
                                        )}
                                        {d.maintenanceReason && (
                                            <div style={{ color: "#6c757d", fontStyle: "italic", fontSize: "0.68rem" }}>
                                                {d.maintenanceReason}
                                            </div>
                                        )}
                                    </div>
                                ))}
                                <Link to="/maintenance" style={{ fontSize: "0.7rem", color: "#6f42c1" }}>
                                    View maintenance log <i className="fa fa-arrow-right ml-1" />
                                </Link>
                            </div>
                        )}
                    </div>

                </div>
            </div>

            <div className={`card-footer py-2 ${collapsed ? "d-none" : ""}`}>
                <div className="starek-card-footer">
                    <Link to="/devices" className="starek-card-footer-link">
                        Manage devices <i className="fa fa-arrow-right" />
                    </Link>
                </div>
            </div>
        </div>
    );
}

export default MachineStatusCard;
